import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";
import { SpinePreview } from "./preview";
import { StaticConfigPanel } from "./panel/StaticConfigPanel";
import { SpineConfigPanel } from "./panel/SpineConfigPanel";
import { ConfigValidation } from "./components/ConfigValidation";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { ImportButton } from "./import";
import { SaveButton } from "./components/SaveButton";
import { UploadAvatar } from "./create-avatar";
import { ExportDropdown } from "./export";
import { SubmitReviewButton } from "./components/SubmitReviewButton";
import {
  processTemplate,
  processStaticLayerTemplate,
} from "./utils/templateLoader";
import { getFetcher } from "api/swr";
import { toast } from "@/components/ui/use-toast";
import useSWR, { mutate } from "swr";
import {
  isStaticSpineTemplateType,
  isChildSpineTemplate,
  type SpineTemplate,
} from "../types/spine-template";
import {
  applyImportedOutfitToStores,
  ensureRelatedTemplatesForOutfit,
  type SpineEditorLocationState,
} from "../lib/outfitFromDrawData";
import { Badge } from "@/components/ui/badge";

const SpineEditor = () => {
  const { tid } = useParams<{ tid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const outfitApplySessionRef = useRef(0);
  const lastTidForOutfitRef = useRef<string | undefined>(undefined);
  const { spineData, setCurrentTid, templateType, templateConfig } =
    useConfigStore();
  const isStaticLayer = isStaticSpineTemplateType(templateType);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const { data: templateMeta } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );
  const isChildTemplate = isChildSpineTemplate(templateMeta);

  const loadOnlineData = async () => {
    if (!tid) return;

    try {
      setIsLoading(true);
      setLoadingMessage("正在获取模板信息...");

      const template = await getFetcher(`/spine-templates/${tid}`);

      const staticLayer = isStaticSpineTemplateType(template.type);

      // 无 Zip：普通 Spine 空模板直接返回；静态图层走独立加载
      if (!template.spineZipUrl && !staticLayer) {
        setIsLoading(false);
        return;
      }

      const {
        setSpineData,
        setTemplateConfig,
        setAllRelatedTemplateIds,
        setLoadedRelatedTemplates,
        setTemplateType,
      } = useConfigStore.getState();

      const {
        setExistingZipUrl,
        setExistingFingerprint,
        setExistingSpineVersion,
        setSpineZipDirty,
        randomizeSkins,
      } = useEditorStore.getState();

      setTemplateType(template.type || "character");

      if (staticLayer) {
        // activeTab 是全局的：从 Spine 模版切过来时可能是 skin/toning 等，静态面板只有 info/selection/children
        const { activeTab, setActiveTab } = useEditorStore.getState();
        const staticTabs = new Set(["info", "selection", "children"]);
        if (!staticTabs.has(activeTab)) {
          setActiveTab("info");
        }
        setLoadingMessage("正在加载静态图层模版...");
        const mainData = await processStaticLayerTemplate(
          tid,
          template,
          setLoadingMessage,
        );
        setExistingZipUrl(null);
        setExistingFingerprint(null);
        setExistingSpineVersion(null);
        setSpineZipDirty(false);
        setSpineData(null);
        setTemplateConfig(mainData.templateConfig);
      } else {
        const mainData = await processTemplate(
          tid,
          template,
          setLoadingMessage,
        );
        setExistingZipUrl(template.spineZipUrl);
        setExistingFingerprint(template.spineFingerprint || null);
        setExistingSpineVersion(template.spineVersion || null);
        // 从线上加载的 Zip（可与当前 tid 无关）不视为待上传
        setSpineZipDirty(false);
        setSpineData(mainData.spineData);
        setTemplateConfig(mainData.templateConfig);
        setTimeout(() => randomizeSkins(), 0);
      }

      const relatedIds = template.relatedTemplateIds || [];
      setAllRelatedTemplateIds(relatedIds);
      setLoadedRelatedTemplates([]);
    } catch (error) {
      console.error("加载线上数据失败:", error);
      toast({
        variant: "destructive",
        title: "加载失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // 刷新：重新从服务器加载数据
  const handleRefresh = async () => {
    const { isDirty } = useEditorStore.getState();
    if (isDirty) {
      const confirmed = window.confirm(
        "有未保存的更改，刷新将丢失这些更改。是否继续？",
      );
      if (!confirmed) return;
    }
    if (tid) {
      await mutate([`/spine-templates/${tid}`]);
    }
    await loadOnlineData();
  };

  // 设置当前 tid 并尝试加载线上数据
  useEffect(() => {
    if (!tid) return;
    // 使用 getState() 获取最新的 store 状态快照
    // 这样做是为了在副作用执行的一瞬间获取真实状态，而不依赖组件渲染时的闭包值
    const {
      currentTid: prevTid,
      spineData: currentData,
      setSpineData,
    } = useConfigStore.getState();

    // 判断是否切换了模版
    const isTemplateChanged = prevTid !== tid;

    if (isTemplateChanged) {
      // 如果切换了模版，立即清空旧数据，避免 UI 闪烁显示旧模版内容
      setSpineData(null);
    }

    // 更新 store 中的当前 TID
    setCurrentTid(tid);

    // 如果切换了模版，或者虽然 ID 没变但 store 里没有数据（例如刷新页面后），则加载数据
    if (isTemplateChanged || !currentData) {
      loadOnlineData();
    }
  }, [tid]);

  useEffect(() => {
    if (lastTidForOutfitRef.current !== tid) {
      lastTidForOutfitRef.current = tid;
      outfitApplySessionRef.current = 0;
    }
  }, [tid]);

  // 从 Avatar 菜单跳转时携带的待应用搭配（location.state）
  useEffect(() => {
    const pending = (location.state as SpineEditorLocationState | null)
      ?.pendingOutfitDrawData;
    if (!tid || isLoading || !templateConfig) return;
    if (!spineData && !isStaticLayer) return;
    if (!pending) return;

    const session = ++outfitApplySessionRef.current;

    void (async () => {
      try {
        await ensureRelatedTemplatesForOutfit(pending, tid);
        if (session !== outfitApplySessionRef.current) return;
        const result = applyImportedOutfitToStores(pending, tid);
        if (result.ok === false) {
          toast({
            variant: "destructive",
            title: "应用搭配失败",
            description: result.message,
          });
          navigate(".", { replace: true, state: {} });
          return;
        }
        toast({
          title: "已应用 Avatar 搭配",
          description: "皮肤、染色与动画已按 drawData 更新",
        });
        navigate(".", { replace: true, state: {} });
      } catch (e) {
        if (session !== outfitApplySessionRef.current) return;
        toast({
          variant: "destructive",
          title: "应用搭配失败",
          description: e instanceof Error ? e.message : "未知错误",
        });
        navigate(".", { replace: true, state: {} });
      }
    })();
  }, [
    tid,
    isLoading,
    templateConfig,
    spineData,
    isStaticLayer,
    location.state,
    navigate,
  ]);

  // 加载中状态
  if (isLoading) {
    return (
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/spine")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-3xl font-bold tracking-tight flex-1">
            Spine Editor - {tid}
          </h2>
        </div>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">
              {loadingMessage || "加载中..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4">
      {/* 标题栏 */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin/spine")}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-3xl font-bold tracking-tight flex-1 flex items-center gap-2">
          <span>Spine Editor {!spineData && `- ${tid}`}</span>
          {isChildTemplate && (
            <Badge variant="outline" className="text-xs font-normal">
              子模版
            </Badge>
          )}
        </h2>

        {/* 操作按钮（仅在有数据时显示） */}
        {(spineData || isStaticLayer) && (
          <div className="flex items-center gap-2">
            <ConfigValidation />
            <SaveButton />
            <Button
              variant="outline"
              size="default"
              onClick={handleRefresh}
              disabled={isLoading}
              title="从服务器刷新数据"
              className="gap-1"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              刷新
            </Button>
            <ImportButton />
            <ExportDropdown />
            <UploadAvatar />
            <SubmitReviewButton />
          </div>
        )}
      </div>

      {/* 空状态：没有线上数据，需要用户导入 */}
      {!spineData && !isStaticLayer ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center space-y-6">
            <div className="text-6xl">🦴</div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">该模板还没有 Spine 数据</h3>
              <p className="text-muted-foreground">请导入 Spine 文件开始配置</p>
            </div>
            <ImportButton variant="default" />
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 h-[calc(100vh-150px)]">
              <SpinePreview />
            </div>

            <div className="flex-[2] h-[calc(100vh-150px)]">
              {isStaticLayer ? <StaticConfigPanel /> : <SpineConfigPanel />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpineEditor;
